import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import { map } from 'rxjs/operators';
import { RouterModule } from '@angular/router';
import { AttendeeComponent } from './attendee/attendee.component';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';

interface UserContainer {
  user: User | null;
  userId: string;
  isHost: boolean;
  isCreator: boolean;
}

@Component({
  selector: 'meeting-analysis-attendees',
  imports: [CommonModule, RouterModule, AttendeeComponent, MatChipsModule, TranslateModule],
  template: `
    @for (uc of userContainers$ | async; track uc.userId) {
      <div class="attendee-item">
        <meeting-analysis-attendee
          [user]="uc.user"
          [large]="large()"
          defaultInformationKey="MEETING_DETAILS.TEAM_MEMBER_NOT_AVAILABLE"
        ></meeting-analysis-attendee>
        @if (uc.isHost) {
          <mat-chip>{{ 'COMMON.HOST' | translate }}</mat-chip>
        }
        @if (uc.isCreator) {
          <mat-chip>{{ 'COMMON.CREATOR' | translate }}</mat-chip>
        }
      </div>
    }
  `,
  styleUrls: ['./attendees.component.scss'],
})
export class AttendeesComponent {
  private iamService = inject(IAMService);
  creator = input<string>('');
  host = input<string>('');
  userIds = input<string[]>([]);
  large = input<boolean>(false);
  userContainers$ = toObservable(this.userIds).pipe(
    switchMap((userIds) => {
      if (userIds.length === 0) {
        return of({ userIds: [] as string[], users: [] as (User | null)[] });
      }
      const userIdentifiers = userIds.map((userId) => ({ userId }) as UserIdentifier);
      return this.iamService.getMultiUsers(userIdentifiers).pipe(map((users) => ({ userIds, users })));
    }),
    map(({ userIds, users }) => {
      const userMap = new Map(users.filter((u): u is User => u !== null).map((u) => [u.userId, u]));
      const containers = userIds.map(
        (userId) =>
          ({
            user: userMap.get(userId) || null,
            userId,
            isHost: userId === this.host(),
            isCreator: userId === this.creator(),
          }) as UserContainer,
      );
      return containers.sort((a, b) => (a.user === null ? 1 : 0) - (b.user === null ? 1 : 0));
    }),
  );
}
